import { useEffect, useRef, useState } from 'react'
import { ArrowSquareOut, Check, Copy, SquaresFour } from './ui/icons'
import { houseNoun } from '../lib/house-noun'
import type { CustomerSegment, SharedSandboxAccess } from '../types'

/**
 * Loads the shared sandbox accounts once per key. Returns undefined while
 * loading (or when key is null) and null when they did not load.
 */
export function useSharedSandboxAccess(key: string | null, load: (() => Promise<SharedSandboxAccess>) | undefined): SharedSandboxAccess | null | undefined {
  const [access, setAccess] = useState<SharedSandboxAccess | null | undefined>(undefined)
  const loadRef = useRef(load)
  loadRef.current = load
  useEffect(() => {
    setAccess(undefined)
    const run = loadRef.current
    if (key === null || !run) return
    let active = true
    run()
      .then((value) => { if (active) setAccess(value) })
      .catch(() => { if (active) setAccess(null) })
    return () => { active = false }
  }, [key])
  return access
}

/**
 * The shared Prentus sandbox accounts: an admin account, a student or
 * jobseeker account and one password. Customer workspaces show it on the
 * Product tab; partners with the setting on see it on Product and Demo Access. The
 * accounts come from the SHARED_SANDBOX_ACCESS Worker secret through an
 * authenticated route, never from the client bundle.
 */
export function SandboxAccessCard({ access, segment = null }: { access: SharedSandboxAccess; segment?: CustomerSegment | null }) {
  const [copied, setCopied] = useState('')
  const noun = houseNoun(segment)
  const accountLabel = `${noun.singular.charAt(0).toUpperCase() + noun.singular.slice(1)} account`

  async function copyValue(label: string, value: string) {
    await navigator.clipboard.writeText(value)
    setCopied(label)
    window.setTimeout(() => setCopied(''), 1600)
  }

  return (
    <section className="sandbox-access" aria-labelledby="sandbox-access-title">
      <header className="sandbox-access-header">
        <span className="sandbox-access-icon"><SquaresFour size={21} /></span>
        <div>
          <h3 id="sandbox-access-title">Experience Prentus in Sandbox</h3>
          <p>Choose the view you want to explore. Both accounts use the same password.</p>
        </div>
        <a className="button button-primary" href={access.loginUrl} target="_blank" rel="noreferrer">Open Prentus<ArrowSquareOut size={16} /></a>
      </header>

      <div className="sandbox-account-grid">
        <SandboxCredential label="Admin account" hint={`Manage ${noun.plural}, workflows, and reporting`} value={access.adminEmail} copied={copied === 'admin'} onCopy={() => void copyValue('admin', access.adminEmail)} />
        <SandboxCredential label={accountLabel} hint={`Experience Prentus as a ${noun.singular}`} value={access.studentEmail} copied={copied === 'student'} onCopy={() => void copyValue('student', access.studentEmail)} />
      </div>

      <div className="sandbox-access-footer">
        <div className="sandbox-password"><span>Password</span><strong>{access.password}</strong><button type="button" aria-label="Copy shared password" onClick={() => void copyValue('password', access.password)}>{copied === 'password' ? <Check size={15} /> : <Copy size={15} />}{copied === 'password' ? 'Copied' : 'Copy'}</button></div>
        <p><strong>Want to test onboarding?</strong> Sign in as the admin, open Directory, and invite your own email as a {noun.singular}.</p>
      </div>
    </section>
  )
}

function SandboxCredential({ label, hint, value, copied, onCopy }: { label: string; hint: string; value: string; copied: boolean; onCopy: () => void }) {
  return (
    <article className="sandbox-account">
      <div><span>{label}</span><small>{hint}</small></div>
      <button type="button" aria-label={`Copy ${label.toLowerCase()} email`} onClick={onCopy}><strong>{value}</strong>{copied ? <Check size={15} /> : <Copy size={15} />}</button>
    </article>
  )
}
